import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Menu, type MenuEntry } from './Menu';

/** Always in the main list. */
const PRIMARY = ['she/her', 'he/him', 'they/them'];
/** One step away, in the "More pronouns" submenu. */
const MORE = ['she/they', 'he/they', 'they/her', 'they/him', 'xe/xem', 'ze/hir', 'any pronouns'];
const KNOWN = [...PRIMARY, ...MORE];

/** Pronoun selector like the gender picker: the common sets up front, more in a submenu, or your own. */
export function PronounPicker({
  value,
  onChange,
  id,
  ariaLabel,
  autoOpen,
  onClose,
  placeholder,
}: {
  value: string | undefined;
  onChange: (pronouns: string | undefined) => void;
  id?: string;
  ariaLabel?: string;
  /** Open the menu right away (inline table editing). */
  autoOpen?: boolean;
  /** Called when the picker is dismissed without a choice. */
  onClose?: () => void;
  /** Shown faintly in place of the value, e.g. "Mixed" when editing several people. */
  placeholder?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ x: number; y: number; w: number } | null>(null);
  const [custom, setCustom] = useState<string | null>(null);
  const current = value?.trim() || '';
  const isCustom = !!current && !KNOWN.includes(current.toLowerCase());

  const open = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setAt({ x: r.left, y: r.bottom + 4, w: r.width });
  };
  useEffect(() => {
    if (autoOpen) open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const item = (p: string) => ({ key: p, label: p, checked: current.toLowerCase() === p, onSelect: () => onChange(p) });
  const items: MenuEntry[] = [
    ...PRIMARY.map(item),
    { key: 'more', label: 'More pronouns', checked: MORE.includes(current.toLowerCase()) || undefined, submenu: MORE.map(item) },
    'separator',
    {
      key: 'custom',
      label: isCustom ? `Custom: ${current}` : 'Custom…',
      checked: isCustom || undefined,
      // Stays "open" so closing the menu here doesn't count as dismissing the picker.
      keepOpen: true,
      onSelect: () => {
        setAt(null);
        setCustom(isCustom ? current : '');
      },
    },
    { key: 'none', label: 'Not recorded', checked: !current, onSelect: () => onChange(undefined) },
  ];

  if (custom !== null) {
    const finish = (save: boolean) => {
      const v = custom.trim();
      setCustom(null);
      if (save) onChange(v || undefined);
      else onClose?.();
    };
    return (
      <input
        id={id}
        className="input cell-input pronoun-custom"
        autoFocus
        value={custom}
        placeholder="e.g., fae/faer"
        aria-label={ariaLabel ?? 'Pronouns'}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setCustom(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false);
          }
        }}
      />
    );
  }

  return (
    <>
      <button
        ref={ref}
        id={id}
        type="button"
        className="select gender-picker"
        aria-haspopup="menu"
        aria-expanded={!!at}
        aria-label={ariaLabel ? `${ariaLabel}: ${placeholder ?? (current || 'not recorded')}` : undefined}
        onClick={() => (at ? setAt(null) : open())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            open();
          }
        }}
      >
        <span className={current && !placeholder ? undefined : 'faint'}>{placeholder ?? (current || 'Not recorded')}</span>
        <ChevronDown size={14} />
      </button>
      {at && (
        <Menu
          items={items}
          x={at.x}
          y={at.y}
          minWidth={Math.max(at.w, 190)}
          ariaLabel="Pronouns"
          onClose={() => {
            setAt(null);
            ref.current?.focus();
            onClose?.();
          }}
        />
      )}
    </>
  );
}
